/**
 * Truncates an Ethereum address for display
 * @param address The full Ethereum address
 * @returns Truncated address (e.g. 0x1234...5678)
 */
export const truncateAddress = (address: string): string => {
  if (address.length < 10) return address
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}

/**
 * Formats a date/time to local (system) timezone with an explicit offset.
 * Example: "2025-01-02 21:54:59.500000+08:00"
 */
export const formatDate = (
  dateInput: string | number | Date | null | undefined,
  options: { precision?: 'microseconds' | 'seconds' } = {}
): string => {
  if (dateInput === null || dateInput === undefined || dateInput === '') {
    return '-'
  }

  const pad2 = (n: number) => String(n).padStart(2, '0')
  const pad3 = (n: number) => String(n).padStart(3, '0')
  const numericPattern = /^[+-]?\d+(\.\d+)?$/
  const toMicrosecondsString = (millis: number, microRemainder: number = 0) =>
    `${pad3(millis)}${pad3(microRemainder)}`

  const parseDateInput = (
    input: string | number | Date
  ): { date: Date; microseconds: string } | null => {
    if (input instanceof Date) {
      const date = input
      if (Number.isNaN(date.getTime())) return null
      return {
        date,
        microseconds: toMicrosecondsString(date.getMilliseconds(), 0),
      }
    }

    if (typeof input === 'string') {
      const trimmed = input.trim()
      if (!trimmed) return null
      if (numericPattern.test(trimmed)) {
        const num = Number(trimmed)
        if (Number.isNaN(num)) return null
        return parseEpochNumber(num)
      }
      const date = new Date(trimmed)
      if (Number.isNaN(date.getTime())) return null
      return {
        date,
        microseconds: toMicrosecondsString(date.getMilliseconds(), 0),
      }
    }

    return parseEpochNumber(input)
  }

  const parseEpochNumber = (
    value: number
  ): { date: Date; microseconds: string } | null => {
    if (Number.isNaN(value)) return null
    const absValue = Math.abs(value)

    // Heuristic: >= 1e14 looks like microseconds since epoch.
    if (absValue >= 1e14) {
      const micro = Math.trunc(value)
      const ms = Math.trunc(micro / 1000)
      const microRemainder = Math.abs(micro % 1000)
      const date = new Date(ms)
      if (Number.isNaN(date.getTime())) return null
      const millis = Math.abs(ms % 1000)
      return {
        date,
        microseconds: toMicrosecondsString(millis, microRemainder),
      }
    }

    const ms = value
    const date = new Date(ms)
    if (Number.isNaN(date.getTime())) return null
    return {
      date,
      microseconds: toMicrosecondsString(date.getMilliseconds(), 0),
    }
  }

  const parsed = parseDateInput(dateInput)
  if (!parsed) {
    return 'Invalid date'
  }

  const { date, microseconds } = parsed
  const year = date.getFullYear()
  const month = pad2(date.getMonth() + 1)
  const day = pad2(date.getDate())
  const hour = pad2(date.getHours())
  const minute = pad2(date.getMinutes())
  const second = pad2(date.getSeconds())

  // getTimezoneOffset(): minutes behind UTC (e.g. China is -480).
  const offsetMinutes = -date.getTimezoneOffset()
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const abs = Math.abs(offsetMinutes)
  const offsetHH = pad2(Math.floor(abs / 60))
  const offsetMM = pad2(abs % 60)

  const precision = options.precision ?? 'microseconds'
  const time =
    precision === 'seconds'
      ? `${year}-${month}-${day} ${hour}:${minute}:${second}`
      : `${year}-${month}-${day} ${hour}:${minute}:${second}.${microseconds}`

  return `${time}${sign}${offsetHH}:${offsetMM}`
}

/**
 * Creates a formatted time string from seconds
 * @param seconds Total seconds
 * @returns Formatted time string (e.g. "10 seconds" or "2 minutes 30 seconds")
 */
export const formatTimeInterval = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`
  }

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60

  if (remainingSeconds === 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`
  }

  return `${minutes} minute${minutes !== 1 ? 's' : ''} ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`
}

/**
 * Formats a number with thousands separators
 * @param value Number to format
 * @returns Formatted number string
 */
export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US').format(value)
}

/**
 * Formats a currency value
 * @param value Number to format
 * @param currency Currency code (default: USD)
 * @returns Formatted currency string
 */
export const formatCurrency = (
  value: number,
  currency: string = 'USD'
): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(value)
}

/**
 * Formats a percentage value
 * @param value Number to format (0-100)
 * @param digits Number of decimal digits
 * @returns Formatted percentage string
 */
export const formatPercentage = (value: number, digits: number = 2): string => {
  return `${value.toFixed(digits)}%`
}

/**
 * Get time ago string (e.g. "2 hours ago")
 * @param dateString ISO date string
 * @returns Time ago string
 */
export const timeAgo = (dateString: string): string => {
  const now = new Date()
  const date = new Date(dateString)
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (seconds < 60) {
    return 'just now'
  }

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
  }

  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`
  }

  const days = Math.floor(hours / 24)
  if (days < 30) {
    return `${days} day${days !== 1 ? 's' : ''} ago`
  }

  const months = Math.floor(days / 30)
  if (months < 12) {
    return `${months} month${months !== 1 ? 's' : ''} ago`
  }

  const years = Math.floor(months / 12)
  return `${years} year${years !== 1 ? 's' : ''} ago`
}

export const removeQuotes = (str: string): string => {
  const len = str.length
  if (str[0] === '"' && str[len - 1] === '"') {
    return str.slice(1, len - 1)
  }
  return str
}

export const formatSnack2Readable = (snackStr: string): string => {
  const words = snackStr.split('_')
  return words.join(' ')
}

export function capitalize(str: string) {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * Check if a record is considered "active" based on its last update time and update frequency.
 * This is shared across tables to keep status rules consistent.
 *
 * Note: `updateFrequency` is treated as seconds to match existing UI logic.
 */
export const isRecordActive = (
  lastUpdated: string,
  updateFrequency: number
): boolean => {
  const lastUpdatedTime = new Date(lastUpdated).getTime()
  const currentTime = new Date().getTime()
  const thresholdMs = updateFrequency * 2 * 1000 // seconds -> ms, doubled
  return currentTime - lastUpdatedTime <= thresholdMs
}

/**
 * Parse a downtime JSON string and format it for display
 * @param downtimeData JSON string or object containing downtime data for different periods
 * @param period The period to extract ('1h', '6h', '12h', '1d', '7d', '30d', 'all')
 * @returns Formatted downtime percentage string
 */
export const parseDowntime = (
  downtimeData:
    | string
    | Record<string, number | string>
    | null
    | undefined,
  period: string = '1d'
): string => {
  try {
    const parsed =
      typeof downtimeData === 'string'
        ? JSON.parse(downtimeData || '{}')
        : downtimeData || {}

    const formatDowntimeValue = (value: number | string | undefined): string => {
      if (typeof value === 'string') {
        const trimmed = value.trim()
        if (!trimmed) return '0%'
        // If backend already provides "0%" style strings, pass through.
        if (/%$/.test(trimmed)) return trimmed
        const n = Number(trimmed)
        if (Number.isNaN(n)) return 'N/A'
        return Number.isInteger(n) ? `${n}%` : `${n.toFixed(1)}%`
      }

      if (typeof value === 'number') {
        return Number.isInteger(value) ? `${value}%` : `${value.toFixed(1)}%`
      }

      return 'N/A'
    }

    if (period === 'all') {
      if (!parsed || typeof parsed !== 'object') return 'N/A'
      const order = ['1h', '6h', '12h', '1d', '7d', '30d']
      const entries = order
        .filter((key) => key in parsed)
        .map((key) => `${key}: ${formatDowntimeValue(parsed?.[key])}`)

      const extras = Object.keys(parsed)
        .filter((key) => !order.includes(key))
        .map((key) => `${key}: ${formatDowntimeValue(parsed?.[key])}`)

      const allEntries = [...entries, ...extras]
      return allEntries.length > 0 ? allEntries.join(', ') : 'N/A'
    }

    return formatDowntimeValue(parsed?.[period] ?? 0)
  } catch (error) {
    return 'N/A'
  }
}

